import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { appLogout, injectSlice } from '@bonanza-social/toolkit/state';
import { CoinsGamingHistoryState } from './coinsGamingTransactionHistory.types';
import { FetchHistoryParams } from '../shared/history.types';
import { isAfterTimestamp } from '@bonanza-social/toolkit/utils';
import { DEFAULT_PAGE_SIZE } from '../shared/history.constants';
import HistoryService from '../../api/HistoryService';
// import { selectGames } from '@bonanza-social/games';

export const fetchCoinsGamingHistory = createAsyncThunk(
  'coinsGamingHistory/fetch',
  async ({ startDate, pageNumber = 1, replace }: FetchHistoryParams) => {
    const response = await HistoryService.getCoinsGamingHistory({
      params: {
        pageNumber: replace ? 1 : pageNumber,
        startDate,
        pageSize: DEFAULT_PAGE_SIZE,
      },
    });

    return response;
  },
);

const initialState: CoinsGamingHistoryState = {
  data: undefined,
  dataState: 'idle',
  lastTimestamp: undefined,
};

const coinsGamingHistory = createSlice({
  name: 'gamingHistory',
  initialState,
  reducers: {
    resetCoinsGamingLastTimestamp(state) {
      state.lastTimestamp = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCoinsGamingHistory.pending, (state) => {
      state.dataState = 'pending';
    });
    builder.addCase(fetchCoinsGamingHistory.fulfilled, (state, action) => {
      const { replace, startDate } = action.meta.arg;
      state.dataState = 'fulfilled';
      state.lastTimestamp = startDate;
      if (replace) {
        state.data = action.payload;
      } else {
        const oldItems = state.data?.items;
        const newItems = action.payload.items;
        state.data = action.payload;
        if (oldItems) {
          state.data.items = [...oldItems, ...newItems];
        }
      }
    });
    builder.addCase(fetchCoinsGamingHistory.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(appLogout, () => {
      return initialState;
    });
  },
});

const withCoinsGamingHistory = injectSlice({
  reducerPath: 'coinsGamingHistory',
  reducer: coinsGamingHistory.reducer,
});

const selectAllItems = withCoinsGamingHistory.selector(
  (state) => state.coinsGamingHistory.data?.items,
);

export const selectCoinsGamingHistoryLastTimestamp =
  withCoinsGamingHistory.selector(
    (state) => state.coinsGamingHistory.lastTimestamp,
  );

export const selectCoinsGamingHistoryItems = createSelector(
  [selectAllItems, (_, timestamp) => timestamp],
  (allItems, timestamp) =>
    (allItems ?? []).filter((item) =>
      isAfterTimestamp(item.createdAt, timestamp),
    ),
);

export const selectCoinsGamingHistoryDataState =
  withCoinsGamingHistory.selector(
    (state) => state.coinsGamingHistory.dataState,
  );

export const selectCoinsGamingHistoryHasNextPage =
  withCoinsGamingHistory.selector(
    (state) => state.coinsGamingHistory.data?.hasNextPage,
  );

export const selectCoinsGamingHistoryPageNumber =
  withCoinsGamingHistory.selector(
    (state) => state.coinsGamingHistory.data?.pageNumber,
  );

export const { resetCoinsGamingLastTimestamp } = coinsGamingHistory.actions;

export default coinsGamingHistory.reducer;
declare global {
  interface LazyLoadedSlices {
    coinsGamingHistory: ReturnType<typeof coinsGamingHistory.reducer>;
  }
}
